import PrimaryButton from "components/common/buttons/PrimaryButton";
import { DialogHeader, DialogBody } from "components/common/dialogs/GenericDialog";
import InputField from "components/common/forms/InputField";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { postRequest } from "lib/core/apiClient";
import { useLocale } from "lib/hooks/useLocale";
import { useState } from "react";
import { mutate } from "swr";
import GyftrIcon from "assests/images/GyftrLogo.png";
import { X } from "@phosphor-icons/react";
import { Icon } from "@shopflo/ui/icon";
import { appendParamsToItemsUrl } from "lib/utils/checkout";
import { usePaymentContext } from "lib/contexts/PaymentProvider";
import { IMAGE_CDN_URL } from "lib/utils/constants";
import OverlaySpinner from "components/common/loaders/OverlaySpinner";

interface VoucherState {
  code: string;
  error: string;
  isLoading: boolean;
}

const AddGiftCardDialog = () => {
  const { t } = useLocale();
  const {
    state: { checkoutModal, checkoutId, billing },
    actions: { setCheckoutModal, updateCheckoutBasedOnCheckoutResponse },
  } = useCheckoutContext();
  const {
    state: { paymentMethods },
    actions: { mutatePayment },
  } = usePaymentContext();

  const shopifyGC = paymentMethods?.find((method) => method.name === "GC" || method.name === "SHOPIFY_GC");

  const [voucherState, setVoucherState] = useState<VoucherState>({
    code: "",
    error: "",
    isLoading: false,
  });

  const handleRedeem = async () => {
    const DISCOUNT_PATH = appendParamsToItemsUrl(`/checkout/${checkoutId}/discount`);
    if (shopifyGC && !shopifyGC.isAvailable && shopifyGC?.unavailabilityReasons.length) {
      setCheckoutModal("GYFTR_SWITCH_COUPON_DIALOG");
      return;
    }

    try {
      setVoucherState((prev) => ({
        ...prev,
        isLoading: true,
      }));
      const response: any = await postRequest(`/checkout/v2/checkout/${checkoutId}/gc/voucher`, {
        voucher_code: voucherState.code,
        voucher_payment_mode: "GIFTCARD",
      });

      if (response.status && response.status === "FAILED") {
        setVoucherState((prev) => ({
          ...prev,
          error: response.error,
        }));
        return;
      }

      updateCheckoutBasedOnCheckoutResponse(response);
      mutate(DISCOUNT_PATH);
      mutatePayment();
      mutate(`/checkout/${checkoutId}/rewards`);
      setCheckoutModal("NONE");
    } catch (err: any) {
      console.error("error", err);
      setVoucherState((prev) => ({
        ...prev,
        error: err?.response?.data?.error,
      }));
    } finally {
      setVoucherState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  return (
    <>
      <DialogBody className="!pb-4 !pt-12">
        <button className="outline-none absolute top-4 right-4  ">
          <X
            className="h-5 w-5 cursor-pointer text-coal-dark"
            onClick={(e) => {
              e.stopPropagation();
              setCheckoutModal("NONE");
            }}
          />
        </button>
        <div className="flex flex-col justify-center items-center">
          <img src={`${IMAGE_CDN_URL}/gift_card.png`} alt="" className="w-24 h-24 justify-center" />
          <h4 className="text-medium text-carbon-dark font-semibold leading-7 text-center">
            {t("save_more_gc")}
          </h4>
          <p className="text-xs text-gray-dark text-center">{t("gc_instant_savings")}</p>
        </div>

        <div className="relative flex w-full flex-col space-y-3 px-6 pb-8 pt-4 mt-4">
          <div className="flex flex-col gap-2">
            <InputField
              type="text"
              label=""
              height="h-11"
              id="voucher_code"
              name="voucherCode"
              value={voucherState.code}
              onChange={(e) =>
                setVoucherState((prev) => ({
                  ...prev,
                  code: e.target.value,
                  error: "",
                }))
              }
              filled={Boolean(voucherState.code?.length)}
              placeholder="Enter shopify gift card code"
              autoComplete="off"
              customClass="placeholder:text-sm placeholder:text-zinc-400/40"
              required={false}
            />
            {voucherState.error && <p className="text-xs px-2 pb-2 text-red-dark">{voucherState.error}</p>}
          </div>

          <PrimaryButton
            buttonText={t("apply")}
            onClick={() => handleRedeem()}
            onTouchStart={() => handleRedeem()}
            height="h-11 rounded-xl"
            isLoading={voucherState.isLoading}
            isDisabled={!voucherState.code}
          />
        </div>
      </DialogBody>
      {voucherState.isLoading && <OverlaySpinner/>}
    </>
  );
};

export default AddGiftCardDialog;
